import QRCode from '@components/QRCode/QRCode';
import { useUser } from '@context/user-context';
import { faCircle } from '@fortawesome/pro-light-svg-icons';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useOpenAppUrl } from '@hooks/use-open-app-url';
import { updateUserDoc } from '@modules/user-doc-helpers';

import styles from './FinishSetupBanner.module.scss';

interface FinishSetupPublicProps {
  desktop?: boolean;
}

interface FinishSetupPrivateProps extends FinishSetupPublicProps {
  handleAppDownloaded: () => Promise<void>;
  url: string;
}

const FinishSetupBanner: React.FC<React.PropsWithChildren<FinishSetupPublicProps>> = ({ desktop }) => {
  const { user } = useUser();

  async function handleAppDownloaded() {
    if (!user) {
      return;
    }

    await updateUserDoc({
      ...user,
      uiEvents: {
        ...user.uiEvents,
        appDownloaded: true,
      },
    });
  }
  const { appStore } = useOpenAppUrl({ includeOpaqueToken: false });
  return (
    <>
      {desktop ? (
        <FinishSetupDesktop handleAppDownloaded={handleAppDownloaded} url={appStore} />
      ) : (
        <FinishSetupTablet handleAppDownloaded={handleAppDownloaded} url={appStore} />
      )}
    </>
  );
};

export const FinishSetupDesktop: React.FC<React.PropsWithChildren<FinishSetupPrivateProps>> = ({
  handleAppDownloaded,
  url,
}) => {
  return (
    <div className={styles['finish-setup-desktop']}>
      <div className={styles['title']}>Finish setup</div>
      <div className={styles['steps']}>
        <div className={styles['step']}>
          <FontAwesomeIcon icon={faCheckCircle} className={`${styles['icon']} ${styles['check-icon']}`} />
          <div className={styles['steps-text']}>Create an account</div>
        </div>
        <div className={styles['step']}>
          <FontAwesomeIcon icon={faCheckCircle} className={`${styles['icon']} ${styles['check-icon']}`} />
          <div className={styles['steps-text']}>Create your card</div>
        </div>
        <div className={styles['step']}>
          <FontAwesomeIcon icon={faCircle} className={`${styles['icon']} ${styles['circle-icon']}`} />
          <div className={styles['steps-text']}>Download the app</div>
        </div>
      </div>
      <div className={styles['actions']}>
        <div className={styles['qr-container']}>
          <div className={styles['qr-text']}>Scan this QR code to get the app on iPhone or Android</div>
          <QRCode value={url} purpose="login" size={150} errorCorrectionLevel="M" />
          <div className={styles['app-download-logos']}>
            <img src="/img/apple-logo.svg" alt="App Store Logo" className={styles['logo-icons']} />
            <img src="/img/play-store-logo.svg" alt="Play Store Logo" className={styles['logo-icons']} />
          </div>
        </div>
        <button className={styles['downloaded-app-btn']} onClick={handleAppDownloaded}>
          I already have the mobile app
        </button>
      </div>
    </div>
  );
};

export const FinishSetupTablet: React.FC<React.PropsWithChildren<FinishSetupPrivateProps>> = ({
  handleAppDownloaded,
  url,
}) => {
  return (
    <div className={styles['finish-setup-tablet']}>
      <div className={styles['qr-code-container']}>
        <QRCode size={100} value={url} />
      </div>
      <div className={styles['setup-text']}>
        <div className={styles['title']}>Finish setup</div>
        <div className={styles['download-info']}>
          <div className={styles['download-text']}>Download the app</div>
          <div className={styles['tablet-icons']}>
            <img src="/img/apple-logo.svg" alt="App Store Logo" className={styles['tablet-icon']} />
            <img src="/img/play-store-logo.svg" alt="Play Store Logo" className={styles['tablet-icon']} />
          </div>
        </div>
        <div className={styles['qr-text']}>Scan this QR code to get the app on iPhone on Android</div>
        <button className={styles['downloaded-app-btn']} onClick={handleAppDownloaded}>
          I already have the mobile app
        </button>
      </div>
    </div>
  );
};

export default FinishSetupBanner;
